import { useRecoilValue } from 'recoil';

import { getListOnDemandAtomByPanelType } from '@/state/atom/translationAtom';
import TranslationConstant from '@/common/constant/TranslationConstant';
import {
    getListRowTranslatedOutline,
    getListRowTranslatedLastMessage
} from '@/component/multiLanguage/handlers/ListTicketOnDemandHandler';
import Utils from '@/common/utils/utils';

const { ON_DEMAND_TRANSLATION_VIEW } = TranslationConstant;

/**
 * Resolve the title and last message a list row should DISPLAY for on-demand
 * list translation (MLT-001-F4).
 *
 * State lives in the per-screen list atom (resolved from panelType) keyed by the
 * current viewKey. When the view-level toggle is ON (activeView = TRANSLATION) and the
 * record has a cached translation, the hook returns the translated values and flags
 * `isShowingTranslation`; otherwise it falls back to the originals passed in.
 *
 * Knowledge rows have no last message (§5.1): pass null/undefined for
 * `originalLastMessage` and the hook returns null for `displayLastMessage`.
 *
 * @param {string} originalTitle - The title the row normally renders.
 * @param {string|null} originalLastMessage - The last message the row normally renders (null for Knowledge).
 * @param {{ panelType: string, viewKey: string, ticketId: string }} params
 *   - panelType: Constant.PANEL_TYPE.* (picks the per-screen atom).
 *   - viewKey: current view key (enum for QA, viewId for helpdesk/knowledge).
 *   - ticketId: the record id (cache key, BR-10).
 * @returns {{
 *   displayTitle: string,
 *   displayLastMessage: string|null,
 *   isShowingTranslation: boolean
 * }}
 */
const useListTicketOnDemandDisplay = (
    originalTitle,
    originalLastMessage,
    { panelType, viewKey, ticketId } = {}
) => {
    const listOnDemandAtom = getListOnDemandAtomByPanelType(panelType);
    const listTicketOnDemand = useRecoilValue(listOnDemandAtom);
    const listRecord = viewKey ? listTicketOnDemand[viewKey] : null;

    const translatedOutline = getListRowTranslatedOutline(listRecord, ticketId);
    const translatedLastMessage = getListRowTranslatedLastMessage(listRecord, ticketId);

    const isTranslationView =
        listRecord?.activeView === ON_DEMAND_TRANSLATION_VIEW.TRANSLATION;

    const displayTitle = isTranslationView && translatedOutline != null
        ? translatedOutline
        : originalTitle;
    const displayLastMessage = isTranslationView && translatedLastMessage != null
        ? translatedLastMessage
        : originalLastMessage;

    return {
        displayTitle,
        displayLastMessage,
        isShowingTranslation:
            isTranslationView && (translatedOutline != null || translatedLastMessage != null)
    };
};

export default useListTicketOnDemandDisplay;
